import { defineStore } from "pinia";
import { ref } from "vue";
import axios from "axios";

export const useUnregisteredTripStore = defineStore("unregisteredTrips", () => {
  const trips = ref([]);
  const meta = ref(null);
  const isLoading = ref(false);
  const isExporting = ref(false);

  const fetchTrips = async (parameters = {}) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const params = {
        page: 1,
        ...parameters,
      };

      const response = await axios.get("/api/unregistered-trips", { params });
      trips.value = response.data.data;
      meta.value = response.data.meta;
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to fetch unregistered trips";
    } finally {
      isLoading.value = false;
    }
  };

  const fetchTripById = async (id) => {
    isLoading.value = true;
    try {
      const response = await axios.get(`/api/unregistered-trips/${id}`);
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to get unregistered trip";
    } finally {
      isLoading.value = false;
    }
  };

  const addTrip = async (tripData) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.post("/api/unregistered-trips", tripData);
      trips.value.push(response.data.data);
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to add unregistered trip";
    } finally {
      isLoading.value = false;
    }
  };

  const updateTrip = async (id, tripData) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.put(
        `/api/unregistered-trips/${id}`,
        tripData,
      );
      const index = trips.value.findIndex((trip) => trip.id === id);
      if (index !== -1) {
        trips.value[index] = response.data.data;
      }
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to unregistered update trip";
    } finally {
      isLoading.value = false;
    }
  };

  const deleteTrip = async (id) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      await axios.delete(`/api/unregistered-trips/${id}`);
      trips.value = trips.value.filter((trip) => trip.id !== id);
      return true;
    } catch (err) {
      throw err.response?.data?.message || "Failed to delete unregistered trip";
    } finally {
      isLoading.value = false;
    }
  };

  // Export trips
  const exportTrips = async (parameters = {}) => {
    if (isExporting.value) return;
    isExporting.value = true;

    try {
      const response = await axios.get("/api/unregistered-trips/export", {
        params: parameters,
        responseType: "blob",
      });

      // Define filename directly in the frontend
      const currentDate = new Date().toISOString().slice(0, 10);
      const fileName = `unregistered_trips_export_${currentDate}.xlsx`;

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting unregistered trips:", err);
    } finally {
      isExporting.value = false;
    }
  };

  return {
    trips,
    meta,
    isLoading,
    fetchTrips,
    fetchTripById,
    addTrip,
    updateTrip,
    deleteTrip,
    isExporting,
    exportTrips,
  };
});
